import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';

import { format, parseISO } from 'date-fns';
import { trackBookingConfirmation } from '../../utils/analyticsEvents';
import CalendarIntegration from './components/CalendarIntegration';
import PreparationResources from './components/PreparationResources';
import BookingSummary from './components/BookingSummary';
import ChatAssistant from '../../components/ChatAssistant';
import EmailPreview from './components/EmailPreview';

const PostBookingConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [bookingData, setBookingData] = useState(null);

  useEffect(() => {
    // Get booking data from navigation state or sessionStorage
    const stateData = location?.state?.bookingData;
    const sessionData = sessionStorage?.getItem('booking_confirmation');
    
    if (stateData) {
      setBookingData(stateData);
      // Track confirmation view
      trackBookingConfirmation(
        stateData?.serviceInterest || 'General Consultation',
        stateData?.id,
        stateData?.source || 'direct'
      );
    } else if (sessionData) {
      try {
        const parsedData = JSON.parse(sessionData);
        setBookingData(parsedData);
        trackBookingConfirmation(
          parsedData?.serviceInterest || 'General Consultation',
          parsedData?.id,
          parsedData?.source || 'direct'
        );
      } catch (err) {
        console.error('Failed to parse booking data:', err);
        navigate('/contact-multi-channel-engagement-hub');
      }
    } else {
      // No booking data found, redirect to contact page
      navigate('/contact-multi-channel-engagement-hub');
    }
  }, [location, navigate]);

  const formatDateTime = (dateString) => {
    try {
      return format(parseISO(dateString), 'EEEE, MMMM dd, yyyy • h:mm a');
    } catch {
      return 'Date not available';
    }
  };

  const formatTime = (dateString) => {
    try {
      return format(parseISO(dateString), 'h:mm a');
    } catch {
      return '';
    }
  };

  if (!bookingData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Icon name="Loader2" size={48} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Booking Confirmed | Northstar Analytics</title>
        <meta
          name="description"
          content="Your consultation has been successfully booked. Prepare for your strategic data consultation with Northstar Analytics."
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#002B45] via-[#003D5C] to-[#002B45]">
        <Header />

        {/* Confirmation Hero Section */}
        <section className="pt-32 pb-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* Success Icon */}
            <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-premium animate-scale-in">
              <Icon name="CheckCircle" size={48} className="text-white" />
            </div>

            {/* Confirmation Message */}
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Consultation Confirmed!
            </h1>
            <p className="text-xl text-white/80 mb-8">
              Your strategic consultation is scheduled. We're excited to help transform your data strategy.
            </p>

            {/* Appointment Details Card */}
            <div className="bg-white rounded-2xl shadow-premium p-8 mb-8 text-left">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-[#002B45] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Calendar" size={24} className="text-[#1FB5A5]" />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-[#002B45] mb-2">
                    {bookingData?.title || bookingData?.serviceInterest || 'Strategic Consultation'}
                  </h2>
                  <p className="text-gray-600">
                    {bookingData?.description || 'Personalized consultation to discuss your data strategy needs'}
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Date & Time */}
                <div className="flex items-start space-x-3">
                  <Icon name="Clock" size={20} className="text-[#1FB5A5] mt-1" />
                  <div>
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Date & Time</p>
                    <p className="text-[#002B45] font-semibold">
                      {formatDateTime(bookingData?.scheduledStart)}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Duration: {bookingData?.durationMinutes || 60} minutes
                    </p>
                  </div>
                </div>

                {/* Meeting Format */}
                <div className="flex items-start space-x-3">
                  <Icon name="Video" size={20} className="text-[#1FB5A5] mt-1" />
                  <div>
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Meeting Format</p>
                    <p className="text-[#002B45] font-semibold">Virtual Consultation</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Video conference link sent via email
                    </p>
                  </div>
                </div>

                {/* Consultant */}
                <div className="flex items-start space-x-3">
                  <Icon name="User" size={20} className="text-[#1FB5A5] mt-1" />
                  <div>
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Your Consultant</p>
                    <p className="text-[#002B45] font-semibold">Northstar Analytics Team</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Expert data strategy consultant
                    </p>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="flex items-start space-x-3">
                  <Icon name="Mail" size={20} className="text-[#1FB5A5] mt-1" />
                  <div>
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Confirmation Sent To</p>
                    <p className="text-[#002B45] font-semibold">{bookingData?.clientEmail}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Check your inbox for details
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* What Happens Next */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8 text-left">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                <Icon name="ArrowRight" size={24} className="text-[#1FB5A5] mr-2" />
                What Happens Next
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-[#1FB5A5] rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">Email Confirmation</p>
                    <p className="text-sm text-white/70">Detailed booking confirmation with meeting link</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-[#1FB5A5] rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">Pre-Consultation Prep</p>
                    <p className="text-sm text-white/70">Review preparation materials below</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-[#1FB5A5] rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">Reminder Notifications</p>
                    <p className="text-sm text-white/70">24-hour and 1-hour reminders</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Calendar Integration */}
        <CalendarIntegration bookingData={bookingData} />

        {/* Email Preview */}
        <EmailPreview bookingData={bookingData} />

        {/* Preparation Resources */}
        <PreparationResources serviceType={bookingData?.serviceInterest} />

        {/* Booking Summary & Actions */}
        <BookingSummary bookingData={bookingData} />

        {/* AI Chat Assistant with booking context */}
        <ChatAssistant context={{ hasBooking: true, bookingData }} />

        <Footer />
      </div>
    </>
  );
};

export default PostBookingConfirmation;